import { supabase } from "@/integrations/supabase/client";

/**
 * Member-invitation acceptance.
 *
 * `accept_org_invitation` is the canonical RPC — it is what the invite email's
 * `?invite_token=` link reaches, and it enforces the invited-email match. It
 * reports failure as a `{ success: false, error }` payload rather than by
 * raising, so `supabase.rpc` RESOLVES on a rejected invitation. Every call site
 * must inspect the result; awaiting the call and moving on treats a wrong-email
 * or expired invite as an acceptance.
 *
 * `accept_org_invite` is a deprecated wrapper around the same function. Do not
 * add callers.
 */
export interface AcceptOrgInvitationResult {
  success: boolean;
  /** Present when `success` is false. Safe to show to the user. */
  error?: string;
  message?: string;
  /** The organization role the invitation granted (super_admin | admin | editor). */
  org_role?: string;
  /**
   * The platform role the account now holds after acceptance (admin | agent).
   * The accept RPC promotes an invited admin to platform `admin`; an editor
   * stays `agent`. The client uses this to seed its role cache and route the
   * new member to a surface they can actually reach.
   */
  platform_role?: string;
}

export const ACCEPT_INVITE_FALLBACK_ERROR =
  "This invite link is invalid or has expired. Please request a new one.";

/**
 * Where a member lands right after accepting an invitation.
 *
 * An admin goes to the admin app (`/admin` → their readiness-based landing); an
 * editor goes straight to the profile editor, a surface they can reach —
 * `/admin/feed` would bounce them, since editors are not platform admins.
 */
export function memberInviteDestination(orgRole: string | null | undefined): string {
  return orgRole === "editor" ? "/admin/profile" : "/admin";
}

/**
 * The `user_metadata.account_type` of an account created through the org-admin
 * signup flow. ProtectedRoute force-redirects an agent-role account carrying it
 * to /admin/setup, and it marks the account org-side for the agent onboarding
 * wizard with no membership lookup needed.
 *
 * Member invitations no longer write an account_type (#786). They used to tag
 * the accepting account "member", which nothing ever cleared, so an agent who
 * left the organization stayed excluded from the agent wizard forever while
 * user_roles still said `agent`. Org-side status for everyone else is now
 * derived from a CURRENT organization_members row (useHasCurrentOrgMembership),
 * which ends with the membership. A legacy "member" value left on existing
 * accounts is deliberately ignored, so those accounts self-heal.
 */
export const ORGANIZATION_ACCOUNT_TYPE = "organization";

/**
 * Whether an account is org-side — organization staff with no agent profile to
 * onboard — and must therefore never be shown the agent onboarding wizard.
 *
 * Org-side when the account signed up as an organization, or when it currently
 * belongs to an organization (any role: super_admin, admin or editor). Anything
 * else — a real agent, an OAuth signup with no account_type, a former member
 * whose membership has ended, a stale legacy "member" tag — is an agent.
 *
 * Returns null while the membership is unresolved (`hasCurrentMembership`
 * null) and the account_type alone does not decide it: callers must not treat
 * an unresolved account as an agent, or the wizard could flash for staff.
 */
export function resolveOrgSideAccount(
  accountType: string | null | undefined,
  hasCurrentMembership: boolean | null
): boolean | null {
  if (accountType === ORGANIZATION_ACCOUNT_TYPE) return true;
  return hasCurrentMembership;
}

export async function acceptOrgInvitation(
  token: string
): Promise<AcceptOrgInvitationResult> {
  // A malformed token never reaches the function body — PostgREST rejects it
  // against the uuid parameter and it arrives here as `error`.
  //
  // supabase-js normally resolves with an `error` rather than rejecting, but a
  // transport-level failure (offline, DNS, an aborted fetch) rejects the
  // promise instead. Every rejection is normalized into the same failure shape
  // so no caller has to also wrap this in a try/catch: an unhandled rejection
  // would leave AcceptInvitePage spinning on "Processing your invitation…"
  // forever, and would abort AuthModal's signup/sign-in flow partway through.
  let data: unknown;
  let error: { message?: string } | null;

  try {
    ({ data, error } = await supabase.rpc("accept_org_invitation", {
      _token: token,
    }));
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err ?? "");
    return { success: false, error: message || ACCEPT_INVITE_FALLBACK_ERROR };
  }

  if (error) {
    return { success: false, error: error.message || ACCEPT_INVITE_FALLBACK_ERROR };
  }

  // The RPC's declared return is generic Json, so the shape is asserted here
  // and validated by the runtime check below rather than by the compiler.
  const result = (data ?? null) as AcceptOrgInvitationResult | null;

  if (!result || typeof result.success !== "boolean") {
    return { success: false, error: ACCEPT_INVITE_FALLBACK_ERROR };
  }

  if (!result.success) {
    return { success: false, error: result.error || ACCEPT_INVITE_FALLBACK_ERROR };
  }

  return result;
}

/**
 * The query parameter the invite email uses: send-org-invite emits
 * `${siteUrl}?invite_token=<token>`.
 */
export const MEMBER_INVITE_PARAM = "invite_token";

/**
 * `org_invitations.token` is `uuid NOT NULL DEFAULT gen_random_uuid()`, so a
 * real token is always a uuid. Anything else cannot be redeemed — PostgREST
 * rejects it against the RPC's uuid parameter before the function body runs.
 */
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The member invitation token in a query string, or null when absent/empty. */
export function readMemberInviteToken(search: string): string | null {
  const token = new URLSearchParams(search).get(MEMBER_INVITE_PARAM);
  return token ? token : null;
}

/**
 * The one acceptance surface. AcceptInvitePage is the single owner of calling
 * `acceptOrgInvitation`; every other surface routes here instead of redeeming
 * the token itself, so exactly one redemption is attempted per invite flow.
 */
export function memberInviteAcceptPath(token: string): string {
  return `/invite?token=${encodeURIComponent(token)}`;
}

/**
 * Where an ALREADY-AUTHENTICATED visitor arriving on a `?invite_token=` URL
 * should be sent: /invite, the dedicated acceptance surface.
 *
 * The invite email links to the site root, and the landing page's only invite
 * handling used to be "open the auth modal if signed out" — so a recipient who
 * was already signed in landed on the marketing page and nothing happened.
 *
 * Returns null when there is no token to redeem, meaning stay put.
 */
export function pendingInviteRedirect(search: string): string | null {
  const token = readMemberInviteToken(search);
  return token ? memberInviteAcceptPath(token) : null;
}

/**
 * The `emailRedirectTo` handed to `supabase.auth.signUp`.
 *
 * With email confirmation ON, signUp returns no session and the invitation can
 * only be redeemed once the recipient clicks the confirmation link. The token
 * therefore has to survive that round trip, so it rides along on the callback
 * URL; without it AuthCallback established the session and routed the new
 * member away with the invitation still pending.
 */
export function signupEmailRedirectUrl(
  origin: string,
  inviteToken?: string | null
): string {
  const callback = `${origin}/auth/callback`;
  if (!inviteToken) return callback;
  return `${callback}?${MEMBER_INVITE_PARAM}=${encodeURIComponent(inviteToken)}`;
}

/**
 * The /invite hand-off for a token that has been out of the app's hands, or
 * null when there is nothing redeemable.
 *
 * Stricter than `pendingInviteRedirect` on purpose: the value here has
 * round-tripped through an email client or browser storage, so a token that
 * could not possibly be redeemed is discarded rather than turned into a doomed
 * /invite visit and an error card.
 */
export function memberInviteRedirectFromToken(
  token: string | null | undefined
): string | null {
  if (!token || !UUID_PATTERN.test(token)) return null;
  return memberInviteAcceptPath(token);
}

/**
 * The intent AuthModal stashes before an OAuth redirect and AuthCallback
 * consumes on the way back. Both invite tokens ride along as distinct fields:
 * they are different invitations, redeemed by different RPCs, landing in
 * different places.
 */
export interface OAuthIntent {
  accountType?: string;
  /** Organization-admin invite (?org_invite=), redeemed by claim_org_admin_role. */
  orgInviteToken?: string;
  /** Member invite (?invite_token=), redeemed only by AcceptInvitePage. */
  memberInviteToken?: string;
  /**
   * Review invitation (/review/:token). Nothing is redeemed for it — it only
   * says where to return so the reviewer can be re-evaluated server-side.
   */
  reviewToken?: string;
  /**
   * A listing claim (/claim-organization/:slug). Nothing is granted for it; it
   * only says where to return. Re-validated on read (src/lib/orgClaimReturn.ts).
   */
  claimOrg?: { slug: string; from?: string | null };
  ts?: number;
}

/** An OAuth round trip that has not completed inside this window is stale. */
export const OAUTH_INTENT_MAX_AGE_MS = 10 * 60 * 1000;

/**
 * Parse a stored OAuth intent, honouring only a fresh one. Never throws — a
 * malformed or expired blob is indistinguishable from no intent at all.
 */
export function parseOAuthIntent(
  raw: string | null | undefined,
  now: number
): OAuthIntent | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const intent = parsed as OAuthIntent;
  if (typeof intent.ts !== "number") return null;
  if (now - intent.ts >= OAUTH_INTENT_MAX_AGE_MS) return null;
  return intent;
}

/**
 * Where /auth/callback sends a newly authenticated visitor when an invitation
 * is waiting, or null to fall through to normal role-based routing.
 *
 * Two carriers reach here, because a member invitation can survive either round
 * trip: email confirmation puts the token on the callback URL, and Google OAuth
 * puts it in the stored intent (the provider controls its own redirect, so
 * there is nowhere to hang a query parameter). The organization-admin invite
 * keeps precedence and its own routing — this returns null for it untouched.
 *
 * Nothing is redeemed here. AcceptInvitePage is the single owner of that.
 */
export function callbackInviteRedirect(
  search: string,
  intent?: OAuthIntent | null
): string | null {
  if (intent?.orgInviteToken) return null;
  return (
    memberInviteRedirectFromToken(readMemberInviteToken(search)) ??
    memberInviteRedirectFromToken(intent?.memberInviteToken)
  );
}

/**
 * What AuthModal does after a successful `signUp`.
 *
 * Modelled as one decision rather than a chain of inline conditions because the
 * ordering is the fix: no session means confirmation is pending and nothing may
 * be redeemed yet, the org-admin invite keeps its own terminal path untouched,
 * and a member invitation is handed to /invite instead of being redeemed here.
 */
export type SignupContinuation =
  /**
   * `hasOrgInvite`: an org-admin invitation was validated but cannot be redeemed
   * until the account is confirmed. The credential is NOT carried through the
   * confirmation redirect on purpose — a query parameter there would reach the
   * host's request logs (the exposure src/lib/orgInviteBootstrap.ts exists to
   * close), and a fragment on `emailRedirectTo` is unreliable because Auth
   * appends its own fragment/query. The invitation stays valid until it
   * expires, so the recipient reopens their invitation link once confirmed and
   * the signed-in landing path redeems it. Both hosted projects autoconfirm
   * (`mailer_autoconfirm = true`), so this branch is not reachable there today.
   */
  | { kind: "confirm_email"; hasMemberInvite: boolean; hasOrgInvite: boolean }
  | { kind: "claim_org_admin" }
  | { kind: "accept_member_invite"; path: string }
  | { kind: "signed_in" };

/**
 * User-facing copy for a failed `claim_org_admin_role` redemption.
 *
 * The RPC RAISEs with stable, machine-readable codes (`email_mismatch`,
 * `expired_token`, `already_consumed`, `invalid_token`, `not_authenticated`).
 * supabase-js surfaces a Postgres RAISE on the RESOLVED `{ error }` — it does
 * NOT reject — so every call site must destructure and inspect `error` rather
 * than relying on a try/catch (which only ever catches a transport failure).
 *
 * The most important case is `email_mismatch`: someone invited at one address
 * who signs in with Google under a different address. Left unhandled, the claim
 * silently fails and the invitee lands as a role-less agent with no signal that
 * anything went wrong.
 */
export interface OrgAdminClaimMessage {
  title: string;
  description: string;
}

export function orgAdminClaimErrorMessage(
  rpcErrorMessage: string | null | undefined
): OrgAdminClaimMessage {
  const message = (rpcErrorMessage ?? "").toLowerCase();

  if (message.includes("email_mismatch")) {
    return {
      title: "This invitation was sent to a different email",
      description:
        "You're signed in with an email that doesn't match your invitation. " +
        "Sign in with the email the invitation was sent to, or ask your " +
        "inviter to re-send it to this address.",
    };
  }
  // A bound invitation replaced by a newer issuance for the same organization
  // (a resend or a corrected address). The old link is dead by an explicit
  // marker, never by clock; the newest email holds the live one.
  if (message.includes("invite_superseded")) {
    return {
      title: "A newer invitation replaced this one",
      description:
        "Open the most recent invitation email you received for this " +
        "organization and use that link instead.",
    };
  }
  if (message.includes("expired")) {
    return {
      title: "This invitation has expired",
      description: "Ask your inviter for a new invitation link.",
    };
  }
  if (message.includes("already_consumed")) {
    return {
      title: "This invitation was already used",
      description:
        "If you didn't finish setting up your organization, ask your inviter " +
        "for a new invitation link.",
    };
  }
  // A bound (pre-activation) invitation whose organization is no longer eligible:
  // it was published, given an owner, or its draft/profile went missing. The
  // recipient can do nothing about any of these — direct them to their inviter.
  if (
    message.includes("org_ineligible") ||
    message.includes("org_has_owner") ||
    message.includes("profile_missing")
  ) {
    return {
      title: "This invitation's organization isn't available for setup",
      description:
        "Its organization can't be set up from this link anymore. Contact " +
        "whoever invited you for a new invitation.",
    };
  }
  // invalid_token, not_authenticated, a transport failure, or anything else.
  return {
    title: "We couldn't verify your invitation",
    description: "Please contact your inviter for a new invitation link.",
  };
}

export function resolveSignupContinuation(input: {
  hasSession: boolean;
  isOrgAdminInvite: boolean;
  search: string;
}): SignupContinuation {
  const token = readMemberInviteToken(input.search);

  // Email confirmation is required: there is no session, so the RPC has no
  // auth.uid() to resolve and redemption has to wait for the callback.
  if (!input.hasSession) {
    return {
      kind: "confirm_email",
      hasMemberInvite: Boolean(token),
      hasOrgInvite: input.isOrgAdminInvite,
    };
  }

  // The separate ?org_invite= organization-admin flow keeps precedence and its
  // existing behaviour. The two links are never issued to the same recipient.
  if (input.isOrgAdminInvite) return { kind: "claim_org_admin" };

  if (token) {
    return { kind: "accept_member_invite", path: memberInviteAcceptPath(token) };
  }

  return { kind: "signed_in" };
}
